/**
 * Single source of truth for name validity across the app.
 *
 * Deliberately a blocklist, not an allowlist. Every allowlist of "permitted
 * name characters" eventually rejects a real person — the ASCII-only rules
 * this replaced rejected `O'Reilly`, `Smith-Jones` and `José` (SQR-14928),
 * and the narrower Unicode allowlist we considered still rejected
 * `St. John Jr.`. So we assert only what we actually know: a name contains
 * at least one letter, and a name does not contain digits. Apostrophes,
 * hyphens, periods, spaces, marks and every script pass for free.
 *
 * `\p{N}` covers non-ASCII digits too, so `٣` is rejected alongside `3`.
 * Unicode property escapes are supported by Hermes on RN 0.81 and by every
 * browser we ship to — no polyfill required.
 *
 * Returns false for an empty or whitespace-only value. Callers that permit
 * an absent name (sign-up's optional last name) must short-circuit before
 * calling this. Sign-up and checkout must agree — see SQR-14928.
 */
const HAS_LETTER = /\p{L}/u;
const HAS_DIGIT = /\p{N}/u;

export function isValidName(name: string | null | undefined): boolean {
  if (!name) return false;

  const trimmed = name.trim();

  return HAS_LETTER.test(trimmed) && !HAS_DIGIT.test(trimmed);
}
